import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/common/SEO';
import Breadcrumbs from '../../components/common/Breadcrumbs';
import RelatedGuides from '../../components/common/RelatedGuides';

export default function AcatenangoHike() {
  const tourComparison = [
    {
      name: 'Budget Tour',
      price: '$50-60',
      groupSize: '15-20 people',
      includes: 'Basic camping gear, meals, guide',
      quality: 'Basic equipment, larger groups',
      recommended: false
    },
    {
      name: 'Standard Tour',
      price: '$65-75',
      groupSize: '10-15 people',
      includes: 'Good camping gear, meals, experienced guide, porter option',
      quality: 'Reliable equipment, good food',
      recommended: true
    },
    {
      name: 'Premium Tour',
      price: '$80-100',
      groupSize: '6-10 people',
      includes: 'High-quality gear, gourmet meals, expert guide, porter included',
      quality: 'Best equipment, small groups, personalized attention',
      recommended: false
    }
  ];

  const packingList = {
    clothing: [
      'Warm jacket (temperatures drop to 25-35°F at night)',
      'Thermal base layers (top and bottom)',
      'Fleece or insulated mid-layer',
      'Waterproof rain jacket',
      'Hiking pants (not jeans)',
      'Warm hat and neck gaiter',
      'Gloves (essential for cold nights)',
      'Extra socks (wool or synthetic)',
      'Comfortable hiking boots (broken in)',
      'Sandals or camp shoes for evening'
    ],
    gear: [
      'Headlamp with extra batteries',
      'Sunglasses and sunscreen',
      'Trekking poles (highly recommended)',
      'Daypack (30-40L)',
      'Water bottles (3L total capacity)',
      'Camera with extra batteries',
      'Power bank for phone',
      'Plastic bags for trash'
    ],
    personal: [
      'Toilet paper and hand sanitizer',
      'Personal medications',
      'Lip balm',
      'Snacks (energy bars, chocolate, nuts)',
      'Cash for tips and extras',
      'Passport or ID copy'
    ]
  };

  const faqs = [
    {
      question: 'Is the Acatenango hike safe?',
      answer: 'Yes, the Acatenango hike is generally safe when done with reputable tour operators. The main risks are altitude sickness, cold temperatures, and weather changes. Experienced guides know the route well, carry emergency equipment, and monitor weather conditions. Choose established tour companies with good safety records, follow your guide\'s instructions, and be honest about your fitness level. The volcano itself is dormant—you\'re viewing active Fuego from a safe distance.'
    },
    {
      question: 'How hard is the Acatenango hike?',
      answer: 'Acatenango is challenging and requires good physical fitness. The hike covers about 7-8 miles with 5,000+ feet of elevation gain to reach 13,045 feet. The steep sections, loose volcanic sand, high altitude, and cold temperatures make it demanding. Most hikers take 5-7 hours to reach the summit camp. If you can hike uphill continuously for 2-3 hours and handle altitude, you can complete it with determination. It\'s harder than Pacaya but achievable for fit beginners.'
    },
    {
      question: 'Can beginners hike Acatenango?',
      answer: 'Yes, but only if you\'re in good physical condition and mentally prepared for a challenging experience. \"Beginner\" in hiking experience is fine, but you need cardiovascular fitness and leg strength. Many first-time volcano hikers successfully complete Acatenango. The key is realistic expectations: it will be difficult, you\'ll be tired, and you might struggle with altitude. If you exercise regularly and can handle steep uphill hiking for several hours, you can do it. Consider Pacaya first if you\'re unsure about your fitness level.'
    },
    {
      question: 'How cold does it get on Acatenango?',
      answer: 'Nighttime temperatures at the summit camp range from 25-35°F (-4 to 2°C), sometimes dropping below freezing with wind chill. It feels even colder due to wind exposure and the fact that you\'re sitting still watching eruptions. During the day, temperatures are mild (50-60°F), but it gets cold quickly after sunset. Proper layering is essential: thermal base layers, fleece, warm jacket, hat, and gloves. Tours provide sleeping bags rated for cold temperatures, but bring your own warm layers for sitting outside at night.'
    },
    {
      question: 'Is Acatenango better than Pacaya?',
      answer: 'Acatenango and Pacaya offer completely different experiences. Acatenango is more challenging, requires overnight camping, and rewards you with spectacular views of Fuego erupting and an unforgettable sunrise. It\'s a bucket-list adventure for fit hikers. Pacaya is easier, takes half a day, and lets you walk on recent lava flows and roast marshmallows over volcanic heat. Choose Acatenango if you want an epic adventure and can handle the physical challenge. Choose Pacaya if you prefer a moderate hike or have limited time. Many travelers do both.'
    },
    {
      question: 'Do I need a guide to hike Acatenango?',
      answer: 'Yes, you must hike Acatenango with a guide—it\'s required by park regulations and essential for safety. The trail is not well-marked, weather can change rapidly, and altitude emergencies require experienced response. Guides also carry camping equipment, cook meals, and know the best viewpoints for Fuego eruptions. Independent hiking is not allowed and would be dangerous. All tours include guides, camping gear, and meals, making the experience much safer and more enjoyable than attempting it alone.'
    },
    {
      question: 'What is the altitude of Acatenango?',
      answer: 'Acatenango\'s summit reaches 13,045 feet (3,976 meters) above sea level. Most tours camp at around 12,300 feet (3,750 meters) with an optional sunrise summit push to the top. For comparison, Antigua sits at 5,000 feet, so you\'re gaining about 8,000 feet of elevation during the hike. The high altitude can cause headaches, shortness of breath, and fatigue. Spend at least one day in Antigua before hiking to acclimatize, stay hydrated, and ascend slowly to minimize altitude sickness symptoms.'
    },
    {
      question: 'Can you see lava every night on Acatenango?',
      answer: 'Fuego volcano erupts regularly—typically every 15-30 minutes—so you\'ll almost certainly see eruptions during your overnight stay. However, visibility depends on weather conditions. Clear nights offer spectacular views of glowing lava explosions lighting up the sky. Cloudy or foggy conditions can obscure the view, though you\'ll still hear the powerful eruptions. The dry season (November-April) provides the best visibility. Even if clouds roll in, the experience of camping on a volcano and hearing Fuego\'s roar is unforgettable. Tours cannot guarantee perfect visibility, but eruptions are constant.'
    }
  ];

  const relatedGuides = [
    {
      title: 'Things to Do in Antigua Guatemala',
      description: 'Complete guide to Antigua\'s best attractions and activities',
      path: '/things-to-do-in-antigua-guatemala',
      icon: 'ri-map-pin-line'
    },
    {
      title: 'Day Trips from Antigua',
      description: 'Best excursions including Pacaya, Lake Atitlán, and more',
      path: '/day-trips-from-antigua-guatemala',
      icon: 'ri-road-map-line'
    },
    {
      title: 'Antigua Travel Guide',
      description: 'Everything you need to plan your Antigua trip',
      path: '/antigua-guatemala-travel-guide',
      icon: 'ri-book-open-line'
    },
    {
      title: 'Antigua Itineraries',
      description: '2, 3, and 5-day itinerary suggestions',
      path: '/itineraries',
      icon: 'ri-calendar-line'
    },
    {
      title: 'Best Time to Visit Antigua',
      description: 'Weather guide and seasonal recommendations',
      path: '/best-time-to-visit-antigua-guatemala',
      icon: 'ri-sun-line'
    },
    {
      title: 'Best Hotels in Antigua',
      description: 'Hotel recommendations and neighborhood guides',
      path: '/best-hotels-in-antigua-guatemala',
      icon: 'ri-hotel-bed-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Acatenango Volcano Hike from Antigua Guatemala: Complete Guide + Tips (2026)"
        description="Plan the Acatenango volcano hike from Antigua Guatemala: difficulty, best tours, packing list, altitude tips, costs, and how to see Fuego eruptions overnight."
        canonical="https://la-antigua-guatemala.com/acatenango-hike-from-antigua"
        type="article"
        keywords="acatenango volcano hike, acatenango hike from antigua guatemala, acatenango overnight hike, acatenango fuego volcano, acatenango tour from antigua, hike acatenango guatemala, is acatenango worth it, acatenango difficulty, acatenango packing list, acatenango altitude"
        lastModified="2026-02-20"
        ogImage={{
          url: "https://public.readdy.ai/ai/img_res/657c57f995ea55dc36d70806d5ed278b.jpg",
          width: "1200",
          height: "630"
        }}
      />
      
      <Navbar darkText />
      
      {/* Hero Section */}
      <section className="relative md:h-[70vh] md:mt-20 mt-16 h-auto py-16 md:py-0 flex items-center justify-center">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/11eee724e718a41561c8853767b537db.jpg"
            alt="Acatenango volcano hike from Antigua Guatemala with views of Fuego eruptions"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Acatenango Volcano Hike from Antigua Guatemala (Complete Guide)</h1>
          <p className="text-xl md:text-2xl mb-8">Everything you need to know about hiking Guatemala's most iconic volcano and witnessing Fuego's spectacular eruptions</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="#tour-comparison" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition whitespace-nowrap cursor-pointer">
              Compare Tours
            </a>
            <a href="#packing-list" className="bg-white hover:bg-gray-100 text-gray-900 px-8 py-4 rounded-lg font-semibold transition whitespace-nowrap cursor-pointer">
              Packing List
            </a>
          </div>
        </div>
      </section>

      {/* Breadcrumbs */}
      <Breadcrumbs 
        items={[
          { label: 'Things to Do', path: '/things-to-do-in-antigua-guatemala' },
          { label: 'Acatenango Hike', path: '/acatenango-hike-from-antigua' }
        ]}
      />

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              This guide covers everything you need to plan the <strong>Acatenango volcano hike from Antigua Guatemala</strong>, including tours, difficulty, packing list, altitude tips, costs, and how to see Fuego eruptions overnight. The Acatenango volcano hike is Guatemala's most iconic adventure—a challenging overnight trek that rewards you with front-row seats to one of the world's most consistently active volcanoes. From your camp at 12,300 feet, you'll watch Fuego volcano erupt every 15-20 minutes, sending glowing lava and ash clouds hundreds of feet into the night sky. Add a spectacular sunrise over the volcanic landscape, and you have an experience that rivals anything in South America.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              This guide covers everything US travelers need to know: how difficult the hike really is, what to pack, which tours to book, altitude considerations, and whether this bucket-list adventure is worth the challenge. Spoiler: for fit hikers seeking an unforgettable experience, absolutely yes.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Acatenango Hike Stats at a Glance</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-50 text-teal-500 rounded-full text-3xl mx-auto mb-4">
                <i className="ri-landscape-line"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">13,045 ft</h3>
              <p className="text-gray-600">Summit Elevation</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-50 text-teal-500 rounded-full text-3xl mx-auto mb-4">
                <i className="ri-arrow-up-line"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">5,000+ ft</h3>
              <p className="text-gray-600">Elevation Gain</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-50 text-teal-500 rounded-full text-3xl mx-auto mb-4">
                <i className="ri-time-line"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">5-7 hours</h3>
              <p className="text-gray-600">Ascent Time</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-50 text-teal-500 rounded-full text-3xl mx-auto mb-4">
                <i className="ri-temp-cold-line"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">25-35°F</h3>
              <p className="text-gray-600">Night Temperature</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Famous */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12">Why the Acatenango Volcano Hike Is So Famous</h2>
          
          <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Front-Row Seats to Fuego's Eruptions</h3>
              <p className="text-gray-700 mb-4">
                Acatenango's neighbor, Fuego volcano, is one of the world's most consistently active volcanoes. From your camp, you're positioned just 1.5 miles away with an unobstructed view of explosive eruptions that occur every 15-30 minutes. Watch glowing lava shoot hundreds of feet into the air, hear the thunderous roar, and feel the ground shake beneath your tent.
              </p>
              <p className="text-gray-700 mb-4">
                This isn't viewing a volcano from a distance—it's an immersive experience where you witness nature's raw power up close (but safely). The nighttime eruptions are particularly spectacular, with orange lava illuminating the dark sky and creating photo opportunities you'll never forget.
              </p>
            </div>
            <div className="w-full h-96">
              <img 
                src="https://readdy.ai/api/search-image?query=Fuego%20volcano%20erupting%20at%20night%20from%20Acatenango%20camp%2C%20massive%20orange%20lava%20explosion%2C%20glowing%20volcanic%20activity%2C%20dramatic%20night%20photography%2C%20camping%20tents%20in%20foreground%2C%20Guatemala%20volcano%20adventure&width=800&height=700&seq=fuego-eruption-night-view&orientation=landscape"
                alt="Fuego volcano erupting at night from Acatenango"
                className="w-full h-full object-cover object-center rounded-lg shadow-md"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
            <div className="order-2 md:order-1 w-full h-96">
              <img 
                src="https://readdy.ai/api/search-image?query=Acatenango%20volcano%20sunrise%20summit%20view%2C%20golden%20hour%20light%20over%20volcanic%20peaks%2C%20hikers%20silhouettes%20at%20summit%2C%20panoramic%20Guatemala%20landscape%2C%20epic%20mountain%20sunrise%20photography%2C%20clouds%20below%20summit&width=800&height=700&seq=acatenango-sunrise-summit&orientation=landscape"
                alt="Sunrise at Acatenango summit"
                className="w-full h-full object-cover object-center rounded-lg shadow-md"
              />
            </div>
            <div className="order-1 md:order-2">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Unforgettable Sunrise Summit</h3>
              <p className="text-gray-700 mb-4">
                Wake up at 4:00 AM for the optional summit push to 13,045 feet. The pre-dawn hike is challenging but short (about 1 hour), and the reward is extraordinary: watching the sun rise over a sea of clouds with multiple volcanoes piercing through, including Agua, Pacaya, and the smoking cone of Fuego.
              </p>
              <p className="text-gray-700 mb-4">
                The summit views are 360 degrees of volcanic landscape—you can see all the way to the Pacific Ocean on clear days. It's a photographer's dream and a moment that makes every difficult step of the ascent worthwhile. Many hikers say the sunrise alone justifies the entire trip to Guatemala.
              </p>
            </div>
          </div>

          <div className="bg-gray-50 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">How Does Acatenango Compare to Pacaya?</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-3">Acatenango</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Overnight camping experience (2 days/1 night)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Challenging hike requiring good fitness</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Views of Fuego erupting all night</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Epic sunrise from 13,000+ feet</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Bucket-list adventure experience</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>$50-80 per person</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-3">Pacaya</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Half-day trip (6 hours total)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Moderate difficulty, family-friendly</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Walk on recent lava flows</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Roast marshmallows over volcanic heat</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Great introduction to volcano hiking</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>$30-40 per person</span>
                  </li>
                </ul>
              </div>
            </div>
            <p className="text-gray-700 mt-6">
              <strong className="text-gray-900">Bottom line:</strong> Acatenango is for adventurous hikers seeking an epic, challenging experience. Pacaya is perfect for those wanting volcano exposure without extreme difficulty. Many travelers do both—Pacaya first to test their volcano hiking legs, then Acatenango for the ultimate adventure.
            </p>
          </div>
        </div>
      </section>

      {/* Difficulty Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">How Difficult Is the Acatenango Hike?</h2>
          
          <div className="bg-white p-8 rounded-lg shadow-sm mb-8">
            <p className="text-xl text-gray-700 mb-6">
              <strong className="text-gray-900">Honest answer:</strong> Acatenango is challenging and should not be underestimated. It's one of the most difficult day hikes in Central America. However, it's achievable for anyone in good physical condition with proper preparation and mental determination.
            </p>
            
            <h3 className="text-2xl font-bold text-gray-900 mb-4">What Makes It Challenging</h3>
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 text-teal-500 rounded-lg text-2xl flex-shrink-0">
                  <i className="ri-arrow-up-line"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Steep, Sustained Climbing</h4>
                  <p className="text-gray-700">The trail gains over 5,000 feet in about 7-8 miles. Much of the ascent is steep and relentless, with few flat sections to rest. The final push to camp involves climbing through loose volcanic sand where you slide backward with each step.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 text-teal-500 rounded-lg text-2xl flex-shrink-0">
                  <i className="ri-lungs-line"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">High Altitude</h4>
                  <p className="text-gray-700">You're climbing from 8,000 feet to over 13,000 feet. The thin air makes breathing harder, your heart rate increases, and you'll tire more quickly than at sea level. Altitude headaches and nausea are common.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 text-teal-500 rounded-lg text-2xl flex-shrink-0">
                  <i className="ri-time-line"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Long Duration</h4>
                  <p className="text-gray-700">Most hikers take 5-7 hours to reach camp, hiking continuously uphill. That's a long time to maintain effort, especially when tired and dealing with altitude. The descent the next day takes 3-4 hours and is hard on knees.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 text-teal-500 rounded-lg text-2xl flex-shrink-0">
                  <i className="ri-temp-cold-line"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Extreme Cold at Night</h4>
                  <p className="text-gray-700">Temperatures drop to 25-35°F with wind chill making it feel colder. You'll be camping at altitude in freezing conditions. Proper gear is essential, and the cold can make sleeping difficult.</p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-4">Fitness Level Required</h3>
            <p className="text-gray-700 mb-4">
              You should be able to hike uphill continuously for 2-3 hours without stopping. If you can do a 10-mile hike with significant elevation gain, you can handle Acatenango with determination. Regular cardio exercise (running, cycling, stair climbing) in the months before helps tremendously.
            </p>
            <p className="text-gray-700 mb-6">
              <strong className="text-gray-900">Who should NOT attempt this hike:</strong> People with heart conditions, severe asthma, or knee problems. If you struggle with basic fitness activities, build up your conditioning first or choose Pacaya instead.
            </p>

            <div className="bg-teal-50 p-6 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-3">💪 Can You Do It?</h4>
              <p className="text-gray-700">
                If you're in decent shape, mentally prepared for a challenge, and willing to push yourself, yes. Thousands of travelers complete this hike every year, including many who aren't hardcore hikers. The key is pacing yourself, staying hydrated, and not giving up when it gets tough. Your guides will support you, and the reward at the top makes every difficult step worthwhile.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Overnight vs Day Hike */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Acatenango Overnight Hike vs Day Hike</h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Overnight Hike (Recommended)</h3>
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Watch Fuego erupt all night</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Experience sunrise from the summit</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">More time to rest and acclimatize</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Full camping experience</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Less rushed pace</span>
                </div>
              </div>
              <p className="text-gray-700 text-sm">
                <strong>Duration:</strong> 2 days / 1 night<br />
                <strong>Cost:</strong> $50-80
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Day Hike (Sunrise Only)</h3>
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Start at 1-2 AM for sunrise summit</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">No camping gear needed</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Saves one day in your itinerary</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Miss the nighttime eruptions</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">More physically demanding (no rest)</span>
                </div>
              </div>
              <p className="text-gray-700 text-sm">
                <strong>Duration:</strong> 12-14 hours<br />
                <strong>Cost:</strong> $40-60
              </p>
            </div>
          </div>

          <div className="bg-teal-50 p-6 rounded-lg">
            <h4 className="font-bold text-gray-900 mb-3">🏕️ Why Overnight Is Better</h4>
            <p className="text-gray-700">
              The overnight experience is what makes Acatenango legendary. Watching Fuego erupt under the stars, sitting around a campfire at 12,000 feet, and waking up to summit for sunrise creates memories that far exceed a sunrise-only day hike. The nighttime eruptions are the highlight—you'll see glowing lava that's invisible during daylight. Plus, camping gives you time to rest before the summit push, making the experience less exhausting. Unless you're extremely short on time, choose the overnight option.
            </p>
          </div>
        </div>
      </section>

      {/* Tour Comparison */}
      <section id="tour-comparison" className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">Best Tours for Acatenango Hike from Antigua</h2>
          <p className="text-lg text-gray-700 mb-12">
            Choosing the right tour company is crucial for safety and enjoyment. Here's what to look for and how different tour levels compare:
          </p>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {tourComparison.map((tour, index) => (
              <div key={index} className={`bg-white p-8 rounded-lg shadow-md ${tour.recommended ? 'ring-2 ring-teal-500' : ''}`}>
                {tour.recommended && (
                  <div className="bg-teal-500 text-white text-sm font-bold px-3 py-1 rounded-full inline-block mb-4">
                    RECOMMENDED
                  </div>
                )}
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{tour.name}</h3>
                <p className="text-3xl font-bold text-teal-500 mb-6">{tour.price}</p>
                <div className="space-y-4 mb-6">
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-1">Group Size</p>
                    <p className="text-gray-700">{tour.groupSize}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-1">Includes</p>
                    <p className="text-gray-700">{tour.includes}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-1">Quality</p>
                    <p className="text-gray-700">{tour.quality}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white p-8 rounded-lg shadow-sm">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">What to Look for in a Tour Company</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-bold text-gray-900 mb-3">Safety Standards</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Experienced guides with first aid training</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Emergency oxygen and communication equipment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Weather monitoring and contingency plans</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Good online reviews mentioning safety</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-3">What's Included</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Transportation from/to Antigua</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>All meals (dinner, breakfast, snacks)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Camping gear (tent, sleeping bag, mat)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Park entrance fees</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Porter option (extra $15-25)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-teal-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Book Your Acatenango Adventure?</h2>
          <p className="text-xl text-white/90 mb-8">Get personalized recommendations for tours, packing, and planning your complete Antigua itinerary.</p>
          <Link to="/plan-my-antigua-trip" className="inline-block bg-white hover:bg-gray-100 text-teal-500 px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap cursor-pointer">
            Plan My Trip
          </Link>
        </div>
      </section>

      {/* Packing List */}
      <section id="packing-list" className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">What to Pack for Acatenango</h2>
          <p className="text-lg text-gray-700 mb-12">
            Proper gear is essential for comfort and safety. Tours provide camping equipment, but you need to bring appropriate clothing and personal items. Here's the complete packing list:
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-50 text-teal-500 rounded-lg text-2xl mb-4">
                <i className="ri-shirt-line"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Clothing</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                {packingList.clothing.map((item, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-50 text-teal-500 rounded-lg text-2xl mb-4">
                <i className="ri-flashlight-line"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Gear</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                {packingList.gear.map((item, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-50 text-teal-500 rounded-lg text-2xl mb-4">
                <i className="ri-first-aid-kit-line"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Personal Items</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                {packingList.personal.map((item, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12 bg-yellow-50 border-l-4 border-yellow-500 p-6">
            <h4 className="font-bold text-gray-900 mb-3">⚠️ Critical Packing Notes</h4>
            <ul className="space-y-2 text-gray-700">
              <li><strong>Layers are everything:</strong> You'll be hot during the climb, freezing at night, and comfortable during the day. Dress in layers you can add/remove easily.</li>
              <li><strong>Gloves are non-negotiable:</strong> Your hands will be painfully cold without them, especially during the sunrise summit push.</li>
              <li><strong>Break in your boots:</strong> New boots will give you blisters. Wear boots you've hiked in before.</li>
              <li><strong>Bring more water than you think:</strong> 3 liters minimum. Dehydration worsens altitude sickness.</li>
              <li><strong>Don't overpack:</strong> Every extra pound makes the climb harder. Stick to essentials.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Altitude & Safety */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Altitude &amp; Safety Tips</h2>
          
          <div className="space-y-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Dealing with Altitude</h3>
              <p className="text-gray-700 mb-4">
                At 13,045 feet, altitude sickness is a real concern. Symptoms include headache, nausea, dizziness, and shortness of breath. Here's how to minimize the risk:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                  <span><strong>Acclimatize in Antigua:</strong> Spend at least one full day at 5,000 feet before hiking. Two days is better.</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                  <span><strong>Hydrate aggressively:</strong> Drink 3-4 liters of water during the hike. Dehydration makes altitude sickness worse.</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                  <span><strong>Climb slowly:</strong> Don't rush. Slow and steady wins this race. Take breaks when needed.</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                  <span><strong>Avoid alcohol:</strong> No drinking the night before. Alcohol worsens altitude effects.</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                  <span><strong>Consider medication:</strong> Some hikers take Diamox (acetazolamide) to prevent altitude sickness. Consult your doctor.</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Weather &amp; Safety Considerations</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                  <span><strong>Dry season is safer:</strong> November-April offers the best weather. Rainy season (May-October) brings afternoon storms, slippery trails, and poor visibility.</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                  <span><strong>Weather changes fast:</strong> Conditions can shift from sunny to foggy in minutes. Trust your guide's decisions about safety.</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                  <span><strong>Fuego is unpredictable:</strong> While viewing from Acatenango is safe, Fuego can have larger eruptions. Guides monitor activity and will evacuate if necessary. For more information on volcanic activity, see <a href="https://www.insivumeh.gob.gt/" target="_blank" rel="nofollow noopener" className="text-teal-500 hover:text-teal-600">INSIVUMEH's official volcano monitoring</a>.</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                  <span><strong>Know your limits:</strong> If you're struggling badly, it's okay to turn back. Your safety matters more than reaching the summit.</span>
                </li>
              </ul>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-6">
              <h4 className="font-bold text-gray-900 mb-3">🚨 Emergency Situations</h4>
              <p className="text-gray-700 mb-3">
                Reputable tour companies carry emergency oxygen, first aid kits, and communication devices. Guides are trained to recognize serious altitude sickness and will evacuate if necessary.
              </p>
              <p className="text-gray-700">
                <strong>Severe altitude sickness symptoms requiring immediate descent:</strong> Confusion, inability to walk straight, severe headache not relieved by medication, fluid in lungs (crackling sound when breathing), or loss of consciousness.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Best Time */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Best Time to Hike Acatenango</h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-teal-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Dry Season (November-April) ✅</h3>
              <p className="text-gray-700 mb-4">
                <strong>Best time to hike.</strong> Clear skies, minimal rain, and excellent visibility for Fuego eruptions and sunrise views. Trails are drier and safer.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                  <span>Peak season: December-March</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                  <span>Best visibility for photos</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                  <span>Safer trail conditions</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-500 flex-shrink-0 mt-0.5"></i>
                  <span>More crowded</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-500 flex-shrink-0 mt-0.5"></i>
                  <span>Higher prices</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-100 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Rainy Season (May-October) ⚠️</h3>
              <p className="text-gray-700 mb-4">
                <strong>Not recommended.</strong> Afternoon storms, muddy trails, and clouds that obscure Fuego views. However, mornings can be clear.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                  <span>Fewer tourists</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                  <span>Lower prices</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 flex-shrink-0 mt-0.5"></i>
                  <span>Lush green landscapes</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-500 flex-shrink-0 mt-0.5"></i>
                  <span>Poor visibility (clouds)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-500 flex-shrink-0 mt-0.5"></i>
                  <span>Slippery, muddy trails</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-500 flex-shrink-0 mt-0.5"></i>
                  <span>Risk of storms at night</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Month-by-Month Quick Guide</h3>
            <div className="space-y-3 text-gray-700">
              <p><strong>November-February:</strong> Best months. Clear, cold, excellent visibility. Book in advance.</p>
              <p><strong>March-April:</strong> Still great but warmer. Semana Santa (Holy Week) is very crowded.</p>
              <p><strong>May-June:</strong> Transition to rainy season. Morning hikes possible but afternoon clouds likely.</p>
              <p><strong>July-October:</strong> Rainy season peak. Only for flexible travelers who can wait for clear weather windows.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Is It Worth It */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Is the Acatenango Hike Worth It?</h2>
          
          <div className="bg-white p-8 rounded-lg shadow-sm mb-8">
            <p className="text-xl text-gray-700 mb-6">
              <strong className="text-gray-900">Short answer: Absolutely, if you're physically prepared and seeking adventure.</strong>
            </p>
            
            <h3 className="text-xl font-bold text-gray-900 mb-4">Who Will Love This Hike</h3>
            <ul className="space-y-2 text-gray-700 mb-6">
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                <span>Adventure travelers seeking bucket-list experiences</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                <span>Hikers who enjoy challenging treks with big rewards</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                <span>Photographers chasing epic volcano shots</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                <span>Nature enthusiasts who want to witness raw volcanic power</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                <span>Travelers who don't mind discomfort for incredible experiences</span>
              </li>
            </ul>

            <h3 className="text-xl font-bold text-gray-900 mb-4">Who Should Skip It</h3>
            <ul className="space-y-2 text-gray-700 mb-6">
              <li className="flex items-start gap-2">
                <i className="ri-close-circle-fill text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                <span>People with heart conditions, severe asthma, or knee problems</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-close-circle-fill text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                <span>Travelers who are completely sedentary or out of shape</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-close-circle-fill text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                <span>Those who hate cold weather or camping</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-close-circle-fill text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                <span>Travelers seeking comfort and luxury experiences</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-close-circle-fill text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                <span>Anyone visiting during rainy season without flexible dates</span>
              </li>
            </ul>

            <h3 className="text-xl font-bold text-gray-900 mb-4">Honest Pros &amp; Cons</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-bold text-teal-500 mb-3">PROS</h4>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>✓ Once-in-a-lifetime volcano experience</li>
                  <li>✓ Spectacular nighttime eruptions</li>
                  <li>✓ Unforgettable sunrise views</li>
                  <li>✓ Sense of accomplishment</li>
                  <li>✓ Incredible photos</li>
                  <li>✓ Affordable ($50-80)</li>
                  <li>✓ Well-organized tours</li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-red-500 mb-3">CONS</h4>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>✗ Physically demanding</li>
                  <li>✗ Altitude sickness risk</li>
                  <li>✗ Extremely cold at night</li>
                  <li>✗ Basic camping conditions</li>
                  <li>✗ Weather can ruin visibility</li>
                  <li>✗ Takes 2 days from itinerary</li>
                  <li>✗ Not suitable for everyone</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-teal-50 p-6 rounded-lg">
            <h4 className="font-bold text-gray-900 mb-3">💭 The Verdict</h4>
            <p className="text-gray-700">
              Most travelers who complete the Acatenango hike rank it as their top Guatemala experience—and often one of the best adventures of their lives. Yes, it's hard. Yes, you'll be cold and tired. But watching Fuego explode under the stars from your camp at 12,000 feet is genuinely magical. If you're in decent shape and willing to push yourself, do it. You'll never forget it. If you're unsure about your fitness, try Pacaya first to gauge your volcano hiking abilities.
            </p>
          </div>
        </div>
      </section>

      {/* Related Guides */}
      <RelatedGuides guides={relatedGuides} />

      {/* Next Steps */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Next Steps</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/itineraries" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer group">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-50 text-teal-500 rounded-lg text-2xl mb-4">
                <i className="ri-calendar-line"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-500 transition">Plan Your Itinerary</h3>
              <p className="text-gray-700">See how to fit Acatenango into your Antigua trip with our 2, 3, and 5-day itineraries.</p>
            </Link>

            <Link to="/guatemala-city-airport-to-antigua" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer group">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-50 text-teal-500 rounded-lg text-2xl mb-4">
                <i className="ri-bus-line"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-500 transition">Airport Transportation</h3>
              <p className="text-gray-700">Learn how to get from Guatemala City airport to Antigua safely and affordably.</p>
            </Link>

            <Link to="/best-hotels-in-antigua-guatemala" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer group">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center text-white text-2xl mb-4">
                <i className="ri-hotel-line text-teal-500"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-500 transition">Where to Stay</h3>
              <p className="text-gray-600 text-sm">Best hotels in Antigua Guatemala</p>
            </Link>

            <Link to="/plan-my-antigua-trip" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer group">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-50 text-teal-500 rounded-lg text-2xl mb-4">
                <i className="ri-map-pin-user-line"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-500 transition">Get Custom Help</h3>
              <p className="text-gray-700">Need personalized recommendations? Get a custom Antigua itinerary based on your interests.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-teal-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Hike Acatenango?</h2>
          <p className="text-xl text-white/90 mb-8">Get personalized recommendations for tours, packing, and planning your complete Antigua adventure.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/plan-my-antigua-trip" className="inline-block bg-white hover:bg-gray-100 text-teal-500 px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap cursor-pointer">
              Plan My Trip
            </Link>
            <Link to="/itineraries" className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap cursor-pointer">
              View Itineraries
            </Link>
          </div>
        </div>
      </section>

      {/* Last Updated */}
      <section className="py-8 bg-white border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-gray-600">
          <p>Written by the La Antigua Guatemala travel team</p>
          <div className="mt-2">
            <Link to="/editorial-policy" className="text-teal-500 hover:text-teal-600 cursor-pointer">Editorial Policy</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}